import { useTasks } from '@/features/tasks';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import {
  Box,
  Link,
  Popover,
  PopoverContent,
  StatusIndicator,
  Tabs,
  TabsPanel,
} from '@customerio/pluma-components/react';
import { useState } from 'react';
import { IconButton } from '../icon-button';
import * as styles from './index.css';
import { ProductUpdatesList } from './product-updates-list';
import { TasksList } from './tasks-list';
import { useProductUpdates } from './use-product-updates';

export function NotificationsDropdown() {
  const { latest: latestUpdates, hasUnviewed, markAsViewed } = useProductUpdates();
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const {
    tasks,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
  } = useTasks({
    workspaceId,
    limit: 5,
  });

  const pendingTasksCount = tasks.filter((task) => task.isPending).length;
  const hasPendingTasks = pendingTasksCount > 0;
  const tasksTabDisabled = workspaceId == null || workspaceId <= 0;

  const [activeTab, setActiveTab] = useState<string>('updates');
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      void refetchTasks();
      const defaultTab = (!hasPendingTasks && hasUnviewed) || tasksTabDisabled ? 'updates' : 'tasks';
      setActiveTab(defaultTab);

      if (defaultTab === 'updates' && hasUnviewed) {
        markAsViewed();
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleTabChange = (value: string | null) => {
    if (value !== null) {
      setActiveTab(value);

      // Mark updates as viewed when switching to updates tab
      if (value === 'updates' && hasUnviewed) {
        markAsViewed();
      }
    }
  };

  const showStatusIndicator = hasUnviewed || hasPendingTasks;
  const statusColor = hasUnviewed ? 'green' : 'yellow';
  const shouldPulse = hasPendingTasks;

  return (
    <Popover isOpen={isOpen} onOpenChange={handleOpenChange} withArrow={false} placement="bottom-end">
      {(context) => (
        <IconButton
          icon="notifications"
          label="Notifications"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          {showStatusIndicator ? (
            <StatusIndicator color={statusColor} isPulsing={shouldPulse} className={styles.statusIndicator} />
          ) : null}
        </IconButton>
      )}

      <PopoverContent className={styles.popoverContent}>
        <Tabs
          tabs={[
            {
              value: 'tasks',
              text: 'Tasks',
              badge: pendingTasksCount > 0 ? String(pendingTasksCount) : undefined,
              isDisabled: tasksTabDisabled,
            },
            {
              value: 'updates',
              text: 'Product updates',
              badge: hasUnviewed ? String(latestUpdates.length) : undefined,
            },
          ]}
          activeTab={tasksTabDisabled ? 'updates' : activeTab}
          onChange={handleTabChange}
        >
          <TabsPanel value="updates">
            <ProductUpdatesList updates={latestUpdates} onClose={handleClose} />
            <Box p="100" display="flex" justifyContent="center" className={styles.bottomLink}>
              <Link
                href="https://docs.customer.io/release-notes/"
                variant="primary"
                isExternal
                icon="exit"
                iconPosition="trailing"
                onClick={handleClose}
              >
                See all product updates and roadmap
              </Link>
            </Box>
          </TabsPanel>

          <TabsPanel value="tasks">
            <TasksList tasks={tasks} isLoading={isLoadingTasks} workspaceId={workspaceId} onClose={handleClose} />
            <Box p="100" display="flex" justifyContent="center" className={styles.bottomLink}>
              <Link
                href={workspaceId != null ? `/workspaces/${workspaceId}/tasks` : '/'}
                variant="primary"
                onClick={handleClose}
              >
                See all tasks
              </Link>
            </Box>
          </TabsPanel>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
